import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SpamSearchResult, useGetLabelledSpamLazyQuery } from '../../../api/client'
import { toastError } from '../../../components/Notification'
import PageMasthead from '../../../components/PageMasthead'
import PlatformDropdown from '../../../components/PlatformDropdown'
import SimpleLoader from '../../../components/SimpleLoader'
import { useTitle } from '../../../components/TitleProvider'
import ErrorCallout from '../../../components/errors/ErrorCallout'
import {
    PROFILE_PARAM_NAME,
    SocialMediaServiceFilterOptions,
    dropDownToGraphQLEnum
} from '../../../models/SocialMediaServiceSearch'
import { SpamContentList } from './SpamContentList'

/**
 * This page lets users search for and remove spam that was incorrectly added to the spam model.
 *
 * It performs a "range" search against the spam embedding.
 *
 * Tips on development:
 * When developing locally the backend spamService won't be able to access the DNS for the
 * data services API. To resolve this you can do several things:
 * - ensure you have a connection to cloud data services by
 *   - start up cloud data services`lk data-services set_desired 1`,
 *   - make sure that DATA_SERVICES_DNS is something like "internal..."
 *   - forward from local host to the cloud `lk data-services forward`
 *   - start up the ui with `lk ui start_local`
 * - use the stub `export STUB_CLASSIFICATION_SERVICE="true"` in your local env to use the stub service which returns two contentIds
 * - use `lk ui start` to use the remote tenmoku API (this assumes it is up to date)
 */
function SpamModelPage() {
    useTitle('Manage spam model')
    const [searchParams] = useSearchParams()
    const socialMediaServiceParam = searchParams.get(
        PROFILE_PARAM_NAME
    ) as keyof typeof SocialMediaServiceFilterOptions
    let socialMediaService = dropDownToGraphQLEnum(socialMediaServiceParam)

    const [searchText, setSearchText] = useState('')
    const [threshold, setThreshold] = useState(80)

    const [fetchLabelledSpam, { loading, error, data }] = useGetLabelledSpamLazyQuery({
        onError: (error) => {
            toastError(error.message)
        }
    })

    async function handleSearch(event: React.MouseEvent) {
        // This stops the page from reloading
        event.preventDefault()
        await fetchLabelledSpam({
            variables: {
                message: searchText,
                platform: socialMediaService,
                threshold: threshold / 100
            }
        })
    }

    const spamIds = data?.getLabelledSpam?.map((i) => i?.contentId!)
    const scores = data?.getLabelledSpam as SpamSearchResult[]

    return (
        <div className="min-h-full bg-ink-50">
            <PageMasthead
                title="Manage spam model"
                subtitle="Search for and remove content from the spam model"
            />
            <div className="px-4 py-5 sm:px-6 lg:px-8">
                <div className="rounded-lg border border-ink-200 bg-white p-6 shadow-sm">
                    <div className="prose prose-stone prose-lg">
                        <p>
                            This page lets you search for and remove content from the spam model.
                            You can also use this page to experiment to find content that might be
                            identified as spam.
                        </p>
                        <p>
                            <strong>NOTE:</strong> items will take 15-30 minutes to be removed from
                            the spam model.
                        </p>
                    </div>
                    <div className="mt-6 flex flex-col">
                        <div className="min-w-0 flex-1  mr-6 mt-3 mb-3">
                            <label htmlFor="comment">
                                Start by entering the phrase you want to search for...
                            </label>
                            <textarea
                                rows={3}
                                id="comment"
                                className="block w-full bg-white hover:bg-gray-100 border border-gray-300 hover:border-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary rounded-md shadow"
                                placeholder=""
                                defaultValue={''}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        </div>
                        <div className="mr-6 mt-3 mb-3">
                            Only show items with a similarity score greater than...
                            <input
                                type="text"
                                className="block bg-white hover:bg-gray-100 border border-gray-300 hover:border-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary rounded-md shadow"
                                id="threshold"
                                defaultValue={80}
                                onChange={(e) => setThreshold(parseFloat(e.target.value))}
                            ></input>
                        </div>
                        <div className="mr-6 mt-3 mb-3">
                            <label>Specify which platforms you are interested in...</label>
                            <PlatformDropdown
                                onChange={() => {
                                    // No action needed here.
                                }}
                            />
                        </div>

                        <div className="mr-6 mt-3 mb-3">
                            <button
                                onClick={handleSearch}
                                className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                            >
                                Search
                            </button>
                        </div>
                    </div>
                    <div className="mt-6">
                        <SimpleLoader loading={loading} />
                        {error ? (
                            <ErrorCallout message="There was an error fetching the spam content" />
                        ) : null}
                        {spamIds && spamIds.length === 0 && <div>No results</div>}
                        {spamIds &&
                            spamIds.length > 0 &&
                            scores &&
                            scores.length > 0 &&
                            !loading &&
                            !error && <SpamContentList spamIds={spamIds} scores={scores} />}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SpamModelPage
